import { Analytics } from '@analytics';
import GreenTick from '@assets/images/Green_Tick.svg';
import TopNav from '@components/Nav/TopNav';
import AssignCardToProfile from '@components/TapToTip/AssignCardToProfile';
import AssignCardToResource from '@components/TapToTip/AssignCardToResource';
import { GroupEntityType } from '@constants';
import { Button, Divider, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useInjectReducer } from '../../utils/injectReducer';
import { useInjectSaga } from '../../utils/injectSaga';
import * as actions from './actions';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import LogoIcon from '../../assets/images/TripAdmitLogo.png';

const primaryButtonClasses =
  'w-[354px] max-w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors cursor-pointer !normal-case disabled:bg-slate-300 disabled:text-white disabled:cursor-not-allowed';

const secondaryButtonClasses =
  'w-[354px] max-w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold px-5 py-2.5 rounded-xl !border !border-solid !border-slate-300 transition-colors cursor-pointer !normal-case';

function TapToTipAssignCard({
  assignCardToUser,
  isAssigned,
  users,
  resources,
  fetchUsers,
  fetchTipDirectResources,
  user
}) {
  useInjectReducer({ key: 'tapToTipAssignCard', reducer });
  useInjectSaga({ key: 'tapToTipAssignCard', saga });

  const [isAssignToUserOpen, setIsAssignToUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [isAssignToResourceOpen, setIsAssignToResourceOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  const intl = useIntl();
  const { cardId } = useParams();
  const history = useHistory();

  useEffect(() => {
    if (!users?.length) {
      fetchUsers();
    }

    if (!resources?.length) {
      fetchTipDirectResources();
    }
  }, []);

  const handleAssignCard = (type, id) => {
    Analytics.track('Claim Card Button Clicked', {
      type
    });
    assignCardToUser({ type, cardId, id });
  };

  const handlePushToTapToTip = () => {
    history.push({
      pathname: `/tipdirect/`
    });
  };

  return (
    <div>
      <TopNav />
      <div className="flex flex-col items-center justify-center w-full p-6 cursor-pointer">
        <div className="w-full sm:w-1/2 flex items-center flex-col justify-center">
          {!isAssigned ? (
            <>
              {!isAssignToUserOpen && !isAssignToResourceOpen && (
                <>
                  <img
                    className="w-[260px] max-w-full mb-[60px]"
                    alt="TripAdmit logo"
                    src={LogoIcon}
                  />
                </>
              )}
              <div className="text-2xl font-bold text-slate-800 tracking-tight mb-2 text-center">
                {intl.formatMessage(messages.unclaimedCardHeader)}
              </div>
              {!isAssignToUserOpen && !isAssignToResourceOpen && (
                <>
                  <div className="text-sm text-slate-500 mb-4 text-center">
                    {intl.formatMessage(messages.claimCardMessage)}
                  </div>
                </>
              )}
              <div className="flex items-center justify-center h-[51px] min-w-full bg-slate-100 border border-solid border-slate-200 rounded-xl font-bold text-slate-800">
                {cardId}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center w-[367px] max-w-full p-6 bg-emerald-50 text-emerald-700 border border-solid border-emerald-200 rounded-3xl shadow-sm">
              <img src={GreenTick} alt="Check Icon" />
              <p className="my-2 font-semibold text-sm">
                {intl.formatMessage(messages.cardAssignedText)}
              </p>
              <p className="m-0 text-center font-medium w-48 text-xs text-emerald-700/80">
                {intl.formatMessage(messages.cardAssignedInfo)}
              </p>
            </div>
          )}
          {!isAssigned && isAssignToUserOpen && (
            <AssignCardToProfile
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              users={users}
            />
          )}
          {!isAssigned && isAssignToResourceOpen && (
            <AssignCardToResource
              selectedResource={selectedResource}
              setSelectedResource={setSelectedResource}
              resources={resources}
            />
          )}

          <div className="flex flex-col">
            {!isAssigned ? (
              <>
                <div className="flex items-center justify-center flex-col mt-8">
                  {isAssignToUserOpen && (
                    <Button
                      disabled={!selectedUser}
                      onClick={() => handleAssignCard(GroupEntityType.user, selectedUser)}
                      className={primaryButtonClasses}>
                      {intl.formatMessage(messages.claimForUserButton)}
                    </Button>
                  )}

                  {isAssignToResourceOpen && (
                    <Button
                      disabled={!selectedResource}
                      onClick={() => handleAssignCard(GroupEntityType.resource, selectedResource)}
                      className={primaryButtonClasses}>
                      {intl.formatMessage(messages.claimForResourceButton)}
                    </Button>
                  )}

                  {!isAssignToUserOpen && !isAssignToResourceOpen && (
                    <>
                      <Button
                        onClick={() => setIsAssignToUserOpen(true)}
                        className={`${primaryButtonClasses} mt-8`}>
                        {intl.formatMessage(messages.assignToUser)}
                      </Button>
                      {user?.role === 'system_admin' && (
                        <Button
                          onClick={() => setIsAssignToResourceOpen(true)}
                          className={`${primaryButtonClasses} mt-8`}>
                          {intl.formatMessage(messages.assignToResource)}
                        </Button>
                      )}
                    </>
                  )}
                </div>

                <div className="flex items-center my-4">
                  <Divider className="w-[45%]" />
                  <Typography className="w-[10%] mx-4 whitespace-nowrap text-center text-slate-400 text-sm">
                    {intl.formatMessage(messages.or)}
                  </Typography>
                  <Divider className="w-[45%]" />
                </div>
                <Button className={primaryButtonClasses} onClick={() => handleAssignCard()}>
                  {intl.formatMessage(messages.claimCard)}
                </Button>
                <Button className={`${secondaryButtonClasses} mt-2`} onClick={handlePushToTapToTip}>
                  {intl.formatMessage(messages.cancel)}
                </Button>
              </>
            ) : (
              <Button className={`${primaryButtonClasses} mt-8`} onClick={handlePushToTapToTip}>
                {intl.formatMessage(messages.goBackToProfile)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  isAssigned: state.tapToTipAssignCard?.isAssigned,
  users: state.tapToTipAssignCard?.users,
  resources: state.tapToTipAssignCard?.resources,
  user: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  assignCardToUser: ({ type, cardId, id }) =>
    dispatch(actions.assignCardToUser({ type, cardId, id })),
  fetchUsers: () => dispatch(actions.fetchUsers()),
  fetchTipDirectResources: () => dispatch(actions.fetchTipDirectResources())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TapToTipAssignCard);
